import { getToken } from "@/utils/auth";
import { emitter } from "../mitt";
import { globalBalanceWebSocket } from "./globalBalanceWebSocket";
import { HTTP_HEADERS } from "@/config/httpHead";

/**
 * 全局开播通知 WebSocket 管理器
 * 监听 /live/start-notification 接口，实现跨浏览器同步
 */
class GlobalStartNotificationWebSocket {
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isDestroyed = false;

  /**
   * 连接 WebSocket
   */
  async connect(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isDestroyed) {
        resolve(false);
        return;
      }

      const token = getToken();
      if (!token) {
        resolve(false);
        return;
      }

      // 如果已有连接，先关闭
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsHost = window.location.host;
      let wsUrl = "";
      if (import.meta.env.VITE_API_BASE_WS) {
        wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/start-notification?token=${token}`;
      } else {
        wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/start-notification?token=${token}`;
      }

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          resolve(true);
        };

        this.ws.onmessage = event => {
          try {
            const data = JSON.parse(event.data);

            // 📋 每次接收到推送数据都通知直播间列表更新
            this.notifyLiveRoomListRefresh(data);

            // 🚀 处理 live_started 类型 - 建立计费 WebSocket 连接并显示气泡
            if (data.type === "live_started") {
              this.handleLiveStarted(data);
            }
            // 处理开播通知
            else if (
              data.type === "live_start" ||
              data.action === "start" ||
              data.status === "live"
            ) {
              this.handleLiveStart(data);
            }
            // 处理结束通知
            else if (
              data.type === "live_end" ||
              data.type === "live_ended" ||
              data.action === "end" ||
              data.status === "ended"
            ) {
              this.handleLiveEnd(data);
            }
            // 处理余额更新通知
            else if (
              data.type === "balance_update" ||
              data.cost !== undefined
            ) {
              this.handleBalanceUpdate(data);
            }
            // 处理其他可能的格式
            else if (data.roomId) {
              // 根据数据内容判断类型
              if (data.cost !== undefined) {
                this.handleBalanceUpdate(data);
              } else {
                this.handleRoomStatusSync(data);
              }
            }
          } catch (error) {}
        };

        this.ws.onerror = error => {
          this.attemptReconnect();
          reject(error);
        };

        this.ws.onclose = event => {
          this.ws = null;

          // 如果不是主动关闭且未达到最大重连次数，则重连
          if (
            !this.isDestroyed &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.isDestroyed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 重置状态
   */
  reset() {
    this.isDestroyed = false;
    this.reconnectAttempts = 0;
  }

  /**
   * 尝试重连
   */
  private attemptReconnect() {
    if (
      this.isDestroyed ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      return;
    }

    this.reconnectAttempts++;
    this.scheduleReconnect();
  }

  /**
   * 安排重连
   */
  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (!this.isDestroyed) {
        this.connect().catch(error => {});
      }
    }, this.reconnectInterval);
  }

  /**
   * 处理直播开始通知
   */
  private async handleLiveStart(data: any) {
    const { roomId } = data;

    // 🚀 发出直播状态变化事件
    emitter.emit("liveStatusChange", {
      action: "start",
      roomId: roomId || "",
      timestamp: Date.now()
    });

    // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
    const liveStatusData = {
      action: "start",
      roomId: roomId || "",
      timestamp: Date.now()
    };
    localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

    // 🔧 修复：确保计费 WebSocket 连接，避免重复连接
    if (!globalBalanceWebSocket.isConnected()) {
      globalBalanceWebSocket.reset();
      await globalBalanceWebSocket.connect();
    } else {
    }
  }

  /**
   * 处理直播已开始通知（live_started）
   */
  private async handleLiveStarted(data: any) {
    const { roomId } = data;

    // 🚀 发出直播状态变化事件
    emitter.emit("liveStatusChange", {
      action: "start",
      roomId: roomId || "",
      timestamp: Date.now()
    });

    // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
    const liveStatusData = {
      action: "start",
      roomId: roomId || "",
      timestamp: Date.now()
    };
    localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

    // 🔧 修复：确保计费 WebSocket 连接，避免重复连接
    if (!globalBalanceWebSocket.isConnected()) {
      globalBalanceWebSocket.reset();
      await globalBalanceWebSocket.connect();
    } else {
    }
  }

  /**
   * 处理直播结束通知
   */
  private handleLiveEnd(data: any) {
    const { roomId } = data;

    // 🚀 发出直播状态变化事件
    emitter.emit("liveStatusChange", {
      action: "end",
      roomId: roomId || "",
      timestamp: Date.now()
    });

    // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
    const liveStatusData = {
      action: "end",
      roomId: roomId || "",
      timestamp: Date.now()
    };
    localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

    // 🔧 修复：发出特定房间的计费 WebSocket 关闭事件，包含 roomId
    const closeData = {
      reason: "直播结束",
      roomId: roomId || "", // 🔧 关键修复：添加 roomId，确保只影响特定房间
      timestamp: Date.now()
    };
    emitter.emit("balanceWebSocketClose", closeData);
    localStorage.setItem("balanceWebSocketClose", JSON.stringify(closeData));
  }

  /**
   * 处理余额更新通知
   */
  private handleBalanceUpdate(data: any) {
    const { cost, remainder, account } = data;

    // 🚀 发出余额更新事件
    emitter.emit("balanceUpdate", {
      cost: cost || 0,
      remainder: remainder || 0,
      account: account || ""
    });
  }

  /**
   * 处理房间状态同步
   */
  private handleRoomStatusSync(data: any) {
    const { roomId, status, roomData } = data;

    // 🚀 发出房间状态同步事件
    emitter.emit("roomStatusSync", {
      roomId: roomId || "",
      status: status || "preparing",
      timestamp: Date.now(),
      roomData: roomData || {}
    });
  }

  /**
   * 通知直播间列表刷新
   */
  private notifyLiveRoomListRefresh(data: any) {
    // 🚀 发出直播间列表刷新事件
    emitter.emit("refreshLiveRoomList", {
      timestamp: Date.now(),
      reason: "收到开播通知推送"
    });
  }

  /**
   * 初始化跨浏览器监听器
   */
  initCrossBrowserListener() {
    // 监听本地存储变化，实现跨浏览器通信
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key === "liveStatusChange") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          // 跨浏览器同步直播状态变化
          emitter.emit("liveStatusChange", data);
        } catch (error) {}
      } else if (event.key === "balanceWebSocketClose") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          // 跨浏览器同步计费 WebSocket 关闭
          emitter.emit("balanceWebSocketClose", data);
        } catch (error) {}
      }
    });
  }
}

export const globalStartNotificationWebSocket =
  new GlobalStartNotificationWebSocket();
